import './ChannelDrawerAttributionFlow.scss';
import cn from 'classnames';
import { CSSProperties, Fragment, ReactNode, useMemo, useState } from 'react';
import { MdMoreHoriz } from 'react-icons/md';
import { ChartContainer } from '@Components/ChartContainer';
import { ChartTooltipV2, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import {
  SPEND_BAR_OPACITY,
  TWO_STAGE_ADDED_LABEL,
  TWO_STAGE_REDUCED_LABEL
} from '../../dtc-commerce-mmm-constants';
import { DashboardData, HierarchyLevelConst } from '../../dtc-commerce-mmm-types';
import { formatFieldName } from '../../hooks/useChannelAggregation';
import { resolveChannelColor } from '../../utils/dtc-commerce-mmm-channel-colors';
import {
  formatCurrency,
  formatPercentage,
  formatWholeNumber
} from '../../utils/dtc-commerce-mmm-format';
import { ChannelIcon } from '../ChannelIcon';
import { CursorTooltipPopover, useCursorTooltip } from '../CursorTooltip';
import { TwoStageBar } from '../TwoStageBar';
import {
  AttributionMetric,
  AttributionMetricType,
  FlowDirection,
  FlowStepKind
} from './channel-drawer-attribution-flow-constants';
import {
  buildFlowSteps,
  computeFlowBreakdown,
  FlowBreakdown,
  flowRowLabel,
  FlowStep,
  metricLabel,
  metricValue,
  redistributionAmount
} from './channel-drawer-attribution-flow-utils';
import { ExpandableChartName } from './channel-drawer-constants';
import { DownloadActions } from './channel-drawer-types';
import { ChannelDrawerExpandableChart } from './ChannelDrawerExpandableChart';

interface ChannelDrawerAttributionFlowProps {
  data: DashboardData;
  subChannelNames: string[];
  parentChannelKey: string;
  isRevenue: boolean;
  download: DownloadActions;
}

const getRedistributionLabel = (featureMediaType: string): string => {
  if (featureMediaType === 'demand_generation') return 'Driven in Demand Capture';
  if (featureMediaType === 'demand_capture') return 'Driven by Demand Generation';
  return 'Net redistribution';
};

interface FlowStyle {
  color: string;
  opacity: number;
  isAdded: boolean;
}

// Inline color for a striped piece: added credit paints the channel color behind white stripes;
// reduced credit draws channel-color stripes on white through currentcolor.
const stripeStyle = (isAdded: boolean, color: string, opacity: number): CSSProperties =>
  isAdded ? { backgroundColor: color, opacity } : { color };

const TitleStack = ({ subtitle }: { subtitle: string }) => (
  <div className='cdaf-title-stack'>
    <div>Inter-Channel Effects</div>
    <div className='cdaf-title-sub'>{subtitle}</div>
  </div>
);

export const ChannelDrawerAttributionFlow = ({
  data,
  subChannelNames,
  parentChannelKey,
  isRevenue,
  download
}: ChannelDrawerAttributionFlowProps) => {
  const [metric, setMetric] = useState<AttributionMetricType>(AttributionMetric.SPEND);
  const featureName = subChannelNames.length === 1 ? subChannelNames[0] : null;
  const color = resolveChannelColor(parentChannelKey, data.channelColors);

  const breakdown = useMemo(
    () => (featureName === null ? null : computeFlowBreakdown(data, featureName)),
    [data, featureName]
  );

  // Nothing to enlarge until a single feature is selected, so expand is disabled alongside download.
  return (
    <ChannelDrawerExpandableChart
      chartName={ExpandableChartName.INTERCHANNEL}
      disabled={breakdown === null}
    >
      {(isExpanded, expandButton) =>
        breakdown === null ? (
          <AttributionFlowEmpty download={download} expandButton={expandButton} />
        ) : (
          <AttributionFlowChart
            breakdown={breakdown}
            color={color}
            isRevenue={isRevenue}
            metric={metric}
            onMetricChange={setMetric}
            download={download}
            isExpanded={isExpanded}
            expandButton={expandButton}
          />
        )
      }
    </ChannelDrawerExpandableChart>
  );
};

interface AttributionFlowEmptyProps {
  download: DownloadActions;
  expandButton: ReactNode;
}

const AttributionFlowEmpty = ({ download, expandButton }: AttributionFlowEmptyProps) => (
  <ChartContainer
    className='channel-drawer-attribution-flow'
    title={<TitleStack subtitle='How a feature’s direct total becomes its actualized total' />}
    viewRightActions
    rightActions={
      <div className='channel-drawer-section-right-actions'>
        <DownloadDropdown disabled onClickOptions={[download.onExcel, download.onPng]} />
        {expandButton}
      </div>
    }
  >
    <div className='cdaf-chart'>
      <div className='cdaf-empty'>
        Select a single feature above to see how its actualized total reflects its direct total
        plus/minus redistribution.
      </div>
    </div>
  </ChartContainer>
);

interface AttributionFlowChartProps {
  breakdown: FlowBreakdown;
  color: string;
  isRevenue: boolean;
  metric: AttributionMetricType;
  onMetricChange: (metric: AttributionMetricType) => void;
  download: DownloadActions;
  isExpanded: boolean;
  expandButton: ReactNode;
}

const AttributionFlowChart = ({
  breakdown,
  color,
  isRevenue,
  metric,
  onMetricChange,
  download,
  isExpanded,
  expandButton
}: AttributionFlowChartProps) => {
  const isSpend = metric === AttributionMetric.SPEND;
  const isCurrency = isSpend || isRevenue;
  const fmtValue = isCurrency ? formatCurrency : formatWholeNumber;
  const label = metricLabel(metric, isRevenue);
  const metricOptions = [AttributionMetric.SPEND, AttributionMetric.CONTRIBUTION].map(key => ({
    key,
    label: metricLabel(key, isRevenue)
  }));
  const steps = buildFlowSteps(breakdown, metric);
  const scaleMax = Math.max(1, ...steps.map(s => s.end));
  const hasPartners = steps.some(
    s => s.kind === FlowStepKind.PARTNER || s.kind === FlowStepKind.OTHERS
  );
  const flowStyle: FlowStyle = {
    color,
    opacity: isSpend ? SPEND_BAR_OPACITY : 1,
    isAdded: breakdown.direction === FlowDirection.ADDED
  };
  const direct = metricValue(breakdown.direct, metric);
  const actualized = metricValue(breakdown.actualized, metric);

  return (
    <ChartContainer
      className='channel-drawer-attribution-flow'
      title={
        <TitleStack
          subtitle={`How ${formatFieldName(breakdown.featureName)}’s direct total becomes its actualized total`}
        />
      }
      viewRightActions
      rightActions={
        <div className='channel-drawer-section-right-actions'>
          <SegmentedControlButton<AttributionMetricType>
            options={metricOptions}
            selectedOption={metric}
            onChange={onMetricChange}
            size={Size.MD}
          />
          <DownloadDropdown onClickOptions={[download.onExcel, () => download.onPng(isExpanded)]} />
          {expandButton}
        </div>
      }
    >
      <div className='cdaf-chart'>
        <FlowEquation
          direct={direct}
          actualized={actualized}
          label={getRedistributionLabel(breakdown.mediaType)}
          fmtValue={fmtValue}
        />
        {hasPartners ? (
          <>
            <div className='cdaf-waterfall'>
              {steps.map((step, i) => (
                <Fragment key={step.key}>
                  {step.kind === FlowStepKind.ACTUALIZED && (
                    <div className='cdaf-total-rule' aria-hidden='true'>
                      <span className='cdaf-total-rule-line' />
                    </div>
                  )}
                  <FlowRow
                    step={step}
                    scaleMax={scaleMax}
                    breakdown={breakdown}
                    metric={metric}
                    metricName={label}
                    fmtValue={fmtValue}
                    flowStyle={flowStyle}
                    tooltipId={`cdaf-tt-${isExpanded ? 'expanded' : 'drawer'}-${i}`}
                  />
                </Fragment>
              ))}
            </div>
            <FlowLegend flowStyle={flowStyle} />
          </>
        ) : (
          <div className='cdaf-empty'>
            No redistribution applied to this channel in the selected window.
          </div>
        )}
      </div>
    </ChartContainer>
  );
};

interface FlowEquationProps {
  direct: number;
  actualized: number;
  label: string;
  fmtValue: (value: number) => string;
}

// "Direct ± redistribution = actualized", in plain amounts.
const FlowEquation = ({ direct, actualized, label, fmtValue }: FlowEquationProps) => (
  <div className='cdaf-equation'>
    <EquationTerm label='Direct' value={fmtValue(direct)} />
    <span className='cdaf-equation-operator'>{actualized >= direct ? '+' : '−'}</span>
    <EquationTerm label={label} value={fmtValue(redistributionAmount(direct, actualized))} />
    <span className='cdaf-equation-operator'>=</span>
    <EquationTerm label='Actualized' value={fmtValue(actualized)} />
  </div>
);

interface EquationTermProps {
  label: string;
  value: string;
}

const EquationTerm = ({ label, value }: EquationTermProps) => (
  <div className='cdaf-equation-term'>
    <div className='cdaf-equation-label'>{label}</div>
    <div className='cdaf-equation-value'>{value}</div>
  </div>
);

interface FlowRowProps {
  step: FlowStep;
  scaleMax: number;
  breakdown: FlowBreakdown;
  metric: AttributionMetricType;
  metricName: string;
  fmtValue: (value: number) => string;
  flowStyle: FlowStyle;
  tooltipId: string;
}

const FlowRow = ({
  step,
  scaleMax,
  breakdown,
  metric,
  metricName,
  fmtValue,
  flowStyle,
  tooltipId
}: FlowRowProps) => {
  const { rowRef, show, target, handlers } = useCursorTooltip();

  return (
    <>
      <div
        ref={rowRef}
        className={cn('cdaf-row', { 'is-actualized': step.kind === FlowStepKind.ACTUALIZED })}
        {...handlers}
      >
        <div className='cdaf-row-label'>
          <StepIcon step={step} />
          <span className='cdaf-row-label-text'>{flowRowLabel(step, breakdown.direction)}</span>
        </div>
        <div className='cdaf-row-track'>
          <StepBar
            step={step}
            direct={metricValue(breakdown.direct, metric)}
            scaleMax={scaleMax}
            flowStyle={flowStyle}
          />
          {step.connectorAt !== null && (
            <span
              className='cdaf-connector'
              style={{ left: `${(step.connectorAt / scaleMax) * 100}%` }}
            />
          )}
        </div>
        <div className='cdaf-row-value'>{fmtValue(step.value)}</div>
      </div>
      <CursorTooltipPopover id={tooltipId} show={show} target={target}>
        <FlowTooltip
          step={step}
          breakdown={breakdown}
          metric={metric}
          metricName={metricName}
          fmtValue={fmtValue}
          flowStyle={flowStyle}
        />
      </CursorTooltipPopover>
    </>
  );
};

const StepIcon = ({ step }: { step: FlowStep }) => {
  if (step.kind === FlowStepKind.OTHERS) {
    return <MdMoreHoriz className='cdaf-row-icon' aria-hidden='true' />;
  }
  if (step.kind === FlowStepKind.PARTNER) {
    return (
      <ChannelIcon channel={step.partners[0].key} level={HierarchyLevelConst.CHANNEL} size={16} />
    );
  }
  return <span className='cdaf-row-icon-spacer' aria-hidden='true' />;
};

interface StepBarProps {
  step: FlowStep;
  direct: number;
  scaleMax: number;
  flowStyle: FlowStyle;
}

// Direct and actualized draw exactly like the bar view's bars; partner pieces float between
// running totals in the same stripes.
const StepBar = ({ step, direct, scaleMax, flowStyle }: StepBarProps) => {
  const { color, opacity, isAdded } = flowStyle;
  if (step.kind === FlowStepKind.DIRECT || step.kind === FlowStepKind.ACTUALIZED) {
    return (
      <TwoStageBar
        direct={step.kind === FlowStepKind.DIRECT ? step.value : direct}
        actualized={step.value}
        maxValue={scaleMax}
        color={color}
        spendOpacity={opacity}
      />
    );
  }
  return (
    <span
      className={cn('cdaf-piece', isAdded ? 'is-added' : 'is-reduced')}
      style={{
        ...stripeStyle(isAdded, color, opacity),
        left: `${(step.start / scaleMax) * 100}%`,
        width: `${((step.end - step.start) / scaleMax) * 100}%`
      }}
    />
  );
};

interface FlowTooltipProps {
  step: FlowStep;
  breakdown: FlowBreakdown;
  metric: AttributionMetricType;
  metricName: string;
  fmtValue: (value: number) => string;
  flowStyle: FlowStyle;
}

// Shows only the metric the toggle is on, so the spend view never mixes in revenue and vice versa.
const FlowTooltip = ({
  step,
  breakdown,
  metric,
  metricName,
  fmtValue,
  flowStyle
}: FlowTooltipProps) => {
  if (step.kind === FlowStepKind.OTHERS) {
    return (
      <ChartTooltipV2
        headers={[step.label]}
        subHeaderLabel={`Each under 1% of direct ${metricName.toLowerCase()}`}
        items={step.partners.map(p => ({
          label: p.label,
          value: fmtValue(metricValue(p, metric)),
          color: 'transparent'
        }))}
      />
    );
  }

  if (step.kind === FlowStepKind.PARTNER) {
    const direct = metricValue(breakdown.direct, metric);
    const items: PayloadItem[] = [
      { label: metricName, value: fmtValue(step.value), color: 'transparent' }
    ];
    if (direct > 0) {
      items.push({
        label: `Share of direct ${metricName.toLowerCase()}`,
        value: formatPercentage((step.value / direct) * 100, 1),
        color: 'transparent'
      });
    }
    const header = flowStyle.isAdded ? `Credit from ${step.label}` : `Credit to ${step.label}`;
    return <ChartTooltipV2 headers={[header]} items={items} />;
  }

  return (
    <ChartTooltipV2
      headers={[step.label]}
      items={[{ label: metricName, value: fmtValue(step.value), color: flowStyle.color }]}
    />
  );
};

const FlowLegend = ({ flowStyle }: { flowStyle: FlowStyle }) => {
  const { color, opacity, isAdded } = flowStyle;
  return (
    <div className='channel-drawer-legend'>
      <span className='channel-drawer-legend-item'>
        <span className='cdaf-legend-swatch' style={{ backgroundColor: color, opacity }} />
        Direct
      </span>
      <span className='channel-drawer-legend-item'>
        <span
          className={cn('cdaf-legend-swatch', isAdded ? 'is-added' : 'is-reduced')}
          style={stripeStyle(isAdded, color, opacity)}
        />
        {isAdded ? TWO_STAGE_ADDED_LABEL : TWO_STAGE_REDUCED_LABEL}
      </span>
    </div>
  );
};
